import { createTheme } from '@mui/material/styles';

// Brand colors — from docs/design/altyor-brand-guide.md
export const colors = {
  yellow: '#FFFA5E',
  yellowHover: '#FFF23F',
  red: '#FF594A',
  redHover: '#E34334',
  bodyText: '#2B2B2B',
  textMuted: '#6B6B6B',
  darkBg: '#14171C',
  white: '#FFFFFF',
  lightGray: '#EDEDED',
  warmBeige: '#F4DEC7',
  peach: '#FAEDE2',
  softGreen: '#CCFFCC',
  kronosAccent: '#EDC8A3',
} as const;

export const theme = createTheme({
  palette: {
    primary: {
      main: colors.red,
      dark: colors.redHover,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.red,
      dark: colors.redHover,
      contrastText: colors.white,
    },
    error: {
      main: colors.red,
    },
    text: {
      primary: colors.bodyText,
      secondary: colors.textMuted,
    },
    background: {
      default: colors.white,
      paper: colors.white,
    },
    divider: colors.lightGray,
  },

  typography: {
    fontFamily: 'Gabarito, Tahoma, Geneva, sans-serif',
    h1: { fontSize: '36px', fontWeight: 600, lineHeight: 1.2 },
    h2: { fontSize: '32px', fontWeight: 600, lineHeight: 1.3 },
    h3: { fontSize: '24px', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '20px', fontWeight: 600, lineHeight: 1.3 },
    h5: { fontSize: '14px', fontWeight: 600, lineHeight: 1.3 },
    h6: { fontSize: '14px', fontWeight: 600, lineHeight: 1.3 },
    body1: { fontSize: '16px', fontWeight: 400, lineHeight: 1.7 },
    body2: { fontSize: '14px', fontWeight: 400, lineHeight: 1.7 },
    button: { fontSize: '14px', fontWeight: 700, lineHeight: 1.6, textTransform: 'none' as const },
    caption: { fontSize: '12px', fontWeight: 500, lineHeight: 1.5 },
  },

  shape: {
    borderRadius: 0,
  },

  components: {
    // Global — no shadows anywhere
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          overflowX: 'hidden',
        },
        body: {
          backgroundColor: colors.white,
          color: colors.bodyText,
          overflowX: 'hidden',
        },
      },
    },

    // Buttons
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: false,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
          textTransform: 'none' as const,
          fontWeight: 700,
          fontSize: '14px',
          // MUI v9 uses separate classes: MuiButton-contained + MuiButton-colorPrimary
          // (no composite MuiButton-containedPrimary). Double-ampersand raises specificity.
          '&&.MuiButton-contained.MuiButton-colorPrimary': {
            backgroundColor: colors.yellow,
            color: '#000000',
            '&:hover': {
              backgroundColor: colors.yellowHover,
            },
          },
          '&&.MuiButton-outlined.MuiButton-colorSecondary': {
            borderColor: colors.red,
            borderWidth: 2,
            color: colors.red,
            '&:hover': {
              borderWidth: 2,
              backgroundColor: 'rgba(255, 89, 74, 0.04)',
            },
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
      },
    },

    // Cards
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: `1px solid ${colors.lightGray}`,
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: colors.peach,
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: 16,
          },
        },
      },
    },

    // Chip — EXCEPTION: 6px border radius
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 700,
          fontSize: '12px',
        },
      },
    },

    // Dialog — EXCEPTION: 12px border radius
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          boxShadow: 'none',
        },
      },
    },

    // Avatar — square (Altyor industrial signature)
    MuiAvatar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },

    // Table
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colors.lightGray,
          '& .MuiTableCell-head': {
            fontWeight: 700,
            borderBottom: `2px solid ${colors.bodyText}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.lightGray}`,
          fontSize: '14px',
        },
      },
    },

    // Inputs
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.red,
            borderWidth: 2,
          },
        },
        notchedOutline: {
          borderColor: colors.lightGray,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined' as const,
      },
    },

    // Tabs
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 400,
          '&.Mui-selected': {
            color: colors.red,
            fontWeight: 600,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: colors.red,
          height: 3,
        },
      },
    },

    // Tooltip
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.darkBg,
          borderRadius: 0,
          fontSize: '12px',
        },
      },
    },

    // Skeleton
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },

    // Accordion
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
        disableGutters: true,
      },
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.lightGray}`,
          '&:hover': {
            backgroundColor: colors.peach,
          },
          '&::before': {
            display: 'none',
          },
        },
      },
    },

    // Switch
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: colors.yellow,
          },
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: colors.yellow,
          },
        },
      },
    },

    // CircularProgress
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: colors.red,
        },
      },
    },

    // LinearProgress
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: colors.lightGray,
        },
      },
    },

    // Autocomplete
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          border: `1px solid ${colors.lightGray}`,
          boxShadow: 'none',
        },
      },
    },

    // Alert / Snackbar
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },

    // Drawer
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },

    // Menu / Popover
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          border: `1px solid ${colors.lightGray}`,
          boxShadow: 'none',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          boxShadow: 'none',
        },
      },
    },

    // Badge
    MuiBadge: {
      styleOverrides: {
        badge: {
          borderRadius: '6px',
          fontWeight: 700,
        },
      },
    },

    // Breadcrumbs
    MuiBreadcrumbs: {
      defaultProps: {
        separator: '\u2192',
      },
      styleOverrides: {
        root: {
          fontSize: '12px',
          fontWeight: 600,
        },
      },
    },

    // Paper — no shadow
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
        },
      },
    },

    // Stepper
    MuiStepIcon: {
      styleOverrides: {
        root: {
          '&.Mui-active': {
            color: colors.yellow,
          },
          '&.Mui-completed': {
            color: colors.softGreen,
          },
        },
      },
    },

    // AppBar
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          borderBottom: `1px solid ${colors.lightGray}`,
          boxShadow: 'none',
        },
      },
    },
  },
});
